import React, { useState } from 'react'
import { 
  Layout, 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Space, 
  Spin,
  Empty,
  Breadcrumb,
  Tag,
  Tabs,
  Descriptions,
  Modal,
     // Carousel removed - not used
     // Rate removed - not used
     // Divider removed - not used
} from 'antd'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  HomeOutlined, 
  DownloadOutlined, 
  PlayCircleOutlined,
  ShopOutlined
} from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { productsApi, siteSettingsApi } from '../../services/api'
import SafeHtmlRenderer from '../../components/SafeHtmlRenderer'

const { Content } = Layout
const { Title, Text } = Typography
// const { TabPane } = Tabs - removed, not used
// const { Panel } = Collapse - removed, not used

const ProductDetailPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [selectedImage, setSelectedImage] = useState(0)
  const [activeTab, setActiveTab] = useState('overview')
  const [videoModalVisible, setVideoModalVisible] = useState(false)
  const [currentVideoUrl, setCurrentVideoUrl] = useState('')

  // Get current language
  const currentLanguage = i18n.language === 'zh' ? 'zh' : 'en'

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (textObj: any, fallback: string = '') => {
    if (!textObj) return fallback
    if (typeof textObj === 'string') return textObj
    if (typeof textObj === 'object' && textObj[currentLanguage]) {
      return textObj[currentLanguage]
    }
    if (typeof textObj === 'object' && textObj.en) {
      return textObj.en
    }
    return fallback
  }

  // Helper function to filter out generic import-generated content
  const filterGenericContent = (content: string) => {
    if (!content) return ''
    
    // Remove the generic OMRON Healthcare description pattern
    const genericPattern = /Model:\s*[A-Za-z0-9-]+\s*\n\nThis is a high-quality medical device from OMRON Healthcare\. For detailed specifications and features, please refer to the product manual or contact our support team\./g
    
    return content.replace(genericPattern, '').trim()
  }

  // Helper function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return ''
    
    // Handle youtube.com/watch?v= format
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/)
    if (watchMatch) {
      return `https://www.youtube.com/embed/${watchMatch[1]}`
    }
    
    // Handle youtu.be/ format
    const shortMatch = url.match(/youtu\.be\/([^?]+)/)
    if (shortMatch) {
      return `https://www.youtube.com/embed/${shortMatch[1]}`
    }
    
    // Handle embed format (already correct)
    if (url.includes('/embed/')) {
      return url
    }
    
    // Return original URL if no match (fallback)
    return url
  }

  // Fetch product details
  const { data: productData, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productsApi.getById(id!),
    enabled: !!id
  })

  // Fetch related products
  const getCategoryForRelated = () => {
    const category = productData?.product?.category;
    if (!category) return null;
    
    // If category is an object with _id, use the ObjectId
    if (typeof category === 'object' && category._id) {
      return category._id;
    }
    
    // If category is a string ObjectId, use it directly
    if (typeof category === 'string') {
      return category;
    }
    
    return null;
  };

  const { data: relatedProducts } = useQuery({
    queryKey: ['related-products', getCategoryForRelated()],
    queryFn: () => productsApi.getAll({
      category: getCategoryForRelated(),
      limit: 4,
      exclude: id,
      sortBy: 'sortOrder',
      order: 'asc'
    }),
    enabled: !!getCategoryForRelated()
  })

  const product = productData?.product

  const handleWhereToBuy = () => {
    // Use the product's online sales URL if available, otherwise navigate to stores page
    if (product?.onlineSalesUrl) {
      window.open(product.onlineSalesUrl, '_blank')
    } else {
      window.location.href = '/stores'
    }
  }



  const handleWatchVideo = () => {
    if (product?.videoUrl) {
      setCurrentVideoUrl(getYouTubeEmbedUrl(product.videoUrl))
      setVideoModalVisible(true)
    }
  }

  if (isLoading) {
    return (
      <Content>
        <div className="container mx-auto px-gutter py-20">
          <div className="text-center">
            <Spin size="large" />
            <div className="mt-4 text-gray-500">{t('products.messages.loadingDetails')}</div>
          </div>
        </div>
      </Content>
    )
  }

  if (error || !product) {
    return (
      <Content>
        <div className="container mx-auto px-gutter py-20">
          <div className="text-center">
            <Empty description={t('products.messages.notFound')} />
            <Link to="/products">
              <Button type="primary" className="mt-4">
                {t('products.actions.backToProducts')}
              </Button>
            </Link>
          </div>
        </div>
      </Content>
    )
  }

  // Create breadcrumb items
  const breadcrumbItems = [
    {
      title: (
        <Link to="/">
          <HomeOutlined /> {t('navigation.home')}
        </Link>
      ),
    },
    {
      title: <Link to="/products">{t('navigation.products')}</Link>,
    },
    {
      title: getLocalizedText(product.name),
    },
  ]

  // Create tab items
  const tabItems = [
    {
      key: 'overview',
      label: t('products.details.overview'),
      children: (
        <Card>
          <Title level={3}>{t('products.details.overviewTitle')}</Title>
          <SafeHtmlRenderer
            htmlContent={filterGenericContent(getLocalizedText(product.content) || getLocalizedText(product.description))}
            className="text-gray-600 text-lg leading-relaxed"
            tag="div"
          />
          
          {product.benefits && product.benefits.length > 0 && (
            <>
              <Title level={4} className="mt-8">{t('products.details.benefits')}</Title>
              <Row gutter={[16, 16]}>
                {product.benefits.map((benefit: any, index: number) => (
                  <Col xs={24} md={12} key={index}>
                    <Card size="small" className="h-full">
                      <Text strong>{getLocalizedText(benefit)}</Text>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Card>
      ),
    },
    {
      key: 'specifications',
      label: t('products.details.specifications'),
      children: (
        <Card>
          <Title level={3}>{t('products.details.specifications')}</Title>
          {product.specifications && product.specifications.length > 0 ? (
            <Descriptions bordered column={1}>
              {product.specifications.map((spec: any, index: number) => (
                <Descriptions.Item 
                  key={index} 
                  label={getLocalizedText(spec.key) || getLocalizedText(spec.name)}
                >
                  {getLocalizedText(spec.value) || getLocalizedText(spec.description)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : (
            <Empty description={t('products.details.noSpecifications')} />
          )}
        </Card>
      ),
    },
    {
      key: 'downloads',
      label: t('products.details.downloads'),
      children: (
        <Card>
          <Title level={3}>{t('products.details.downloadsResources')}</Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Card 
                hoverable
                className="text-center"
                cover={<DownloadOutlined className="text-4xl text-primary pt-8" />}
              >
                <Card.Meta
                  title={t('products.details.userManual')}
                  description={t('products.details.userManualDescription')}
                />
                <Button 
                  type="primary" 
                  className="mt-4" 
                  block
                  href={product.manualUrl}
                  target="_blank"
                  disabled={!product.manualUrl}
                >
                  {t('common.download')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card 
                hoverable
                className="text-center"
                cover={<PlayCircleOutlined className="text-4xl text-primary pt-8" />}
              >
                <Card.Meta
                  title={t('products.details.howToVideo')}
                  description={t('products.details.howToVideoDescription')}
                />
                <Button 
                  type="primary" 
                  className="mt-4" 
                  block
                  onClick={handleWatchVideo}
                  disabled={!product.videoUrl}
                >
                  {t('products.actions.watchVideo')}
                </Button>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card 
                hoverable
                className="text-center"
                cover={<DownloadOutlined className="text-4xl text-primary pt-8" />}
              >
                <Card.Meta
                  title={t('products.details.quickStart')}
                  description={t('products.details.quickStartDescription')}
                />
                <Button type="primary" className="mt-4" block>
                  {t('common.download')}
                </Button>
              </Card>
            </Col>
          </Row>
        </Card>
      ),
    },
  ]

  return (
    <>
      <Helmet>
        <title>{getLocalizedText(product.name)} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={filterGenericContent(getLocalizedText(product.description))} />
      </Helmet>

      <Content>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="container mx-auto px-gutter">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {/* Product Header */}
        <section className="py-12">
          <div className="container mx-auto px-gutter">
            <Row gutter={[48, 32]}>
              {/* Product Images */}
              <Col xs={24} lg={12}>
                <div className="product-images">
                  {product.images && product.images.length > 0 ? (
                    <>
                      <div className="main-image mb-4">
                        <div className="w-full h-96 bg-white rounded-lg flex items-center justify-center p-4 overflow-hidden">
                          <img
                            src={product.images[selectedImage]?.url}
                            alt={getLocalizedText(product.name)}
                            className="max-w-full max-h-full object-contain rounded-lg"
                          />
                        </div>
                      </div>
                      {product.images.length > 1 && (
                        <Row gutter={[8, 8]}>
                          {product.images.map((image: any, index: number) => (
                            <Col span={6} key={index}>
                              <div
                                className={`cursor-pointer rounded-lg overflow-hidden border-2 ${
                                  selectedImage === index ? 'border-primary' : 'border-gray-200'
                                }`}
                                onClick={() => setSelectedImage(index)}
                              >
                                <img
                                  src={image.url}
                                  alt={getLocalizedText(product.name)}
                                  className="w-full h-20 object-cover"
                                />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-96 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-gray-400">{t('common.noImageAvailable')}</span>
                    </div>
                  )}
                </div>
              </Col>

              {/* Product Info */}
              <Col xs={24} lg={12}>
                <div className="product-info">
                  <Space direction="vertical" size="large" className="w-full">
                    {/* Category and Tags */}
                    <div>
                      {product.category && (
                        <Tag color="blue" className="mb-2">
                          {typeof product.category === 'object' && product.category.name
                            ? getLocalizedText(product.category.name, 'Category')
                            : 'Category'
                          }
                        </Tag>
                      )}
                      {product.tags && product.tags.length > 0 && (
                        <div className="mt-2">
                          {product.tags.map((tag: any, index: number) => (
                            <Tag key={index} color="green" className="mb-1 mr-1">
                              #{getLocalizedText(tag, 'Tag')}
                            </Tag>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Product Name */}
                    <Title level={1} className="mb-0">
                      {getLocalizedText(product.name)}
                    </Title>

                    {/* Key Features */}
                    {product.features && product.features.length > 0 && (
                      <div>
                        <Title level={4}>{t('products.details.features')}</Title>
                        <ul className="list-disc list-inside text-gray-600 space-y-1">
                          {product.features.slice(0, 5).map((feature: any, index: number) => (
                            <li key={index}>{getLocalizedText(feature)}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <Space size="middle" className="w-full">
                      <Button 
                        type="primary" 
                        size="large" 
                        icon={<ShopOutlined />}
                        onClick={handleWhereToBuy}
                      >
                        {t('products.actions.whereToBuy')}
                      </Button>
                    </Space>

                  </Space>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        {/* Product Details Tabs */}
        <section className="py-8 bg-gray-50">
          <div className="container mx-auto px-gutter">
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab} 
              size="large"
              items={tabItems}
            />
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts?.products && relatedProducts.products.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-gutter">
              <Title level={2} className="text-center mb-12">
                {t('products.details.relatedProducts')}
              </Title>
              <Row gutter={[24, 24]}>
                {relatedProducts.products.slice(0, 4).map((relatedProduct: any) => (
                  <Col xs={24} sm={12} lg={6} key={relatedProduct._id}>
                    <Card
                      hoverable
                      cover={
                        <div className="h-48 bg-white flex items-center justify-center overflow-hidden p-6">
                          {relatedProduct.images?.[0]?.url ? (
                            <img 
                              src={relatedProduct.images[0].url} 
                              alt={getLocalizedText(relatedProduct.images[0].alt) || getLocalizedText(relatedProduct.name)}
                              className="max-w-full max-h-full object-contain rounded my-2"
                            />
                          ) : (
                            <span className="text-gray-400">{t('common.noImage')}</span>
                          )}
                        </div>
                      }
                      className="h-full"
                    >
                      <Card.Meta
                        title={getLocalizedText(relatedProduct.name)}
                      />
                      <div className="mt-4 text-center">
                        <Button type="primary" ghost>
                          <Link to={`/products/${relatedProduct._id}`}>
                            {t('common.viewMore')}
                          </Link>
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </section>
        )}
      </Content>

      {/* Video Modal */}
      <Modal
        title={t('products.details.productDemoVideo')}
        open={videoModalVisible}
        onCancel={() => setVideoModalVisible(false)}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        {currentVideoUrl && (
          <div className="aspect-video">
            <iframe
              width="100%"
              height="400"
              src={currentVideoUrl}
              title={t('products.details.productDemoVideo')}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        )}
      </Modal>
    </>
  )
}

export default ProductDetailPage
